import useDistinctValuesQuery from '@hooks/api/queries/archivist/useArchivistDistinctQuery';
import QueryKeys from '@src/constants/queryKeys';
import { filterObject } from '@src/types/Archivist';
import { useMemo } from 'react';
import {
  DeviceTypeCategory,
  FILTER_FIELDS,
  FilterField,
  FilterOptions,
  FilterState,
} from '../types';

interface MultiFieldResponse {
  deviceType?: (string | null)[];
  parentDeviceId?: (string | null)[];
  deviceId?: (string | null)[];
  pointName?: (string | null)[];
}

interface UseMultiFieldDistinctReturn {
  options: FilterOptions;
  isLoading: boolean;
}

const BASE_FILTERS: filterObject[] = [{ field: 'isParsed', value: 'true' }];

const sortValues = (values: (string | null)[] | undefined): string[] =>
  (values ?? [])
    .filter((v): v is string => v != null && v !== '')
    .sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
    );

const buildFiltersUpTo = (
  filters: FilterState,
  upToField: FilterField,
): filterObject[] => {
  const result: filterObject[] = [...BASE_FILTERS];

  for (const field of FILTER_FIELDS) {
    if (field === upToField) {
      break;
    }
    if (filters[field]) {
      result.push({ field, value: filters[field] });
    }
  }
  return result;
};

export const useMultiFieldDistinct = (
  filters: FilterState,
): UseMultiFieldDistinctReturn => {
  const isRackCategory = filters.deviceType === DeviceTypeCategory.RACK;
  const hasDeviceType = !!filters.deviceType;
  const hasParentDeviceId = !!filters.parentDeviceId;
  const hasDeviceId = !!filters.deviceId;

  const categoryFilters = buildFiltersUpTo(
    filters,
    FilterField.PARENT_DEVICE_ID,
  );
  const parentDeviceFilters = buildFiltersUpTo(filters, FilterField.DEVICE_ID);
  const deviceIdFilters = buildFiltersUpTo(filters, FilterField.POINT_NAME);

  const { data: deviceTypeData, isLoading: deviceTypeLoading } =
    useDistinctValuesQuery({
      params: {
        multi_field: [FilterField.DEVICE_TYPE],
        filters: BASE_FILTERS,
      },
      options: {
        enabled: true,
        queryKeys: [QueryKeys.distinctValues, FilterField.DEVICE_TYPE],
      },
    }) as { data: MultiFieldResponse | undefined; isLoading: boolean };

  const rackCategoryFields: FilterField[] = [
    FilterField.PARENT_DEVICE_ID,
    FilterField.DEVICE_ID,
    FilterField.POINT_NAME,
  ];
  const { data: rackCategoryData, isLoading: rackCategoryLoading } =
    useDistinctValuesQuery({
      params: {
        multi_field: rackCategoryFields,
        filters: categoryFilters,
      },
      options: {
        enabled: hasDeviceType && isRackCategory && !hasParentDeviceId,
        queryKeys: [
          QueryKeys.distinctValues,
          'rack-category',
          JSON.stringify(categoryFilters),
        ],
      },
    }) as { data: MultiFieldResponse | undefined; isLoading: boolean };

  const nonRackCategoryFields: FilterField[] = [
    FilterField.DEVICE_ID,
    FilterField.POINT_NAME,
  ];
  const { data: nonRackCategoryData, isLoading: nonRackCategoryLoading } =
    useDistinctValuesQuery({
      params: {
        multi_field: nonRackCategoryFields,
        filters: categoryFilters,
      },
      options: {
        enabled: hasDeviceType && !isRackCategory && !hasDeviceId,
        queryKeys: [
          QueryKeys.distinctValues,
          'non-rack-category',
          JSON.stringify(categoryFilters),
        ],
      },
    }) as { data: MultiFieldResponse | undefined; isLoading: boolean };

  const afterParentDeviceFields: FilterField[] = [
    FilterField.DEVICE_ID,
    FilterField.POINT_NAME,
  ];
  const { data: afterParentDeviceData, isLoading: afterParentDeviceLoading } =
    useDistinctValuesQuery({
      params: {
        multi_field: afterParentDeviceFields,
        filters: parentDeviceFilters,
      },
      options: {
        enabled: isRackCategory && hasParentDeviceId && !hasDeviceId,
        queryKeys: [
          QueryKeys.distinctValues,
          'after-parent-device',
          JSON.stringify(parentDeviceFilters),
        ],
      },
    }) as { data: MultiFieldResponse | undefined; isLoading: boolean };

  const { data: pointNameOnlyData, isLoading: pointNameOnlyLoading } =
    useDistinctValuesQuery({
      params: {
        multi_field: [FilterField.POINT_NAME],
        filters: deviceIdFilters,
      },
      options: {
        enabled: hasDeviceId,
        queryKeys: [
          QueryKeys.distinctValues,
          FilterField.POINT_NAME,
          JSON.stringify(deviceIdFilters),
        ],
      },
    }) as { data: MultiFieldResponse | undefined; isLoading: boolean };

  const options: FilterOptions = useMemo(() => {
    const getDeviceIdOptions = (): string[] => {
      if (isRackCategory) {
        return sortValues(
          hasParentDeviceId
            ? afterParentDeviceData?.deviceId
            : rackCategoryData?.deviceId,
        );
      }
      return sortValues(nonRackCategoryData?.deviceId);
    };

    const getPointNameOptions = (): string[] => {
      if (hasDeviceId) {
        return sortValues(pointNameOnlyData?.pointName);
      }
      if (isRackCategory) {
        return sortValues(
          hasParentDeviceId
            ? afterParentDeviceData?.pointName
            : rackCategoryData?.pointName,
        );
      }
      return sortValues(nonRackCategoryData?.pointName);
    };

    return {
      deviceType: sortValues(deviceTypeData?.deviceType),
      parentDeviceId: sortValues(rackCategoryData?.parentDeviceId),
      deviceId: getDeviceIdOptions(),
      pointName: getPointNameOptions(),
    };
  }, [
    deviceTypeData,
    rackCategoryData,
    nonRackCategoryData,
    afterParentDeviceData,
    pointNameOnlyData,
    isRackCategory,
    hasParentDeviceId,
    hasDeviceId,
  ]);

  const isLoading =
    deviceTypeLoading ||
    (isRackCategory && !hasParentDeviceId && rackCategoryLoading) ||
    (hasDeviceType &&
      !isRackCategory &&
      !hasDeviceId &&
      nonRackCategoryLoading) ||
    (isRackCategory &&
      hasParentDeviceId &&
      !hasDeviceId &&
      afterParentDeviceLoading) ||
    (hasDeviceId && pointNameOnlyLoading);

  return { options, isLoading };
};

export default useMultiFieldDistinct;
